import React, { useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useT } from '../utils/i18n';
function RingTrace({
  elementSize,
  delay,
  duration,
  isInView,
  startAngle
}: {
  elementSize: number;
  delay: number;
  duration: number;
  isInView: boolean;
  startAngle: number;
}) {
  const svgSize = elementSize + 4;
  const radius = elementSize / 2 + 1;
  const circumference = 2 * Math.PI * radius;
  return <svg width={svgSize} height={svgSize} className="absolute pointer-events-none z-20" style={{
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) rotate(${startAngle}deg)`
  }} data-id="element-1117" data-mp="lktiwfmuz8l0">
      <motion.circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke="rgba(46, 138, 68, 0.25)" strokeWidth={2} strokeLinecap="round" initial={{
      strokeDasharray: circumference,
      strokeDashoffset: circumference
    }} animate={isInView ? {
      strokeDashoffset: 0
    } : {
      strokeDashoffset: circumference
    }} transition={{
      duration,
      ease: [0.25, 0.1, 0.25, 1],
      delay
    }} data-id="element-1118" data-mp="crgh0o6tgwms" />
    </svg>;
}
export function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const t = useT();
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-100px'
  });
  const steps = [{
    number: '01',
    title: t('how.step1.title'),
    description: t('how.step1.desc')
  }, {
    number: '02',
    title: t('how.step2.title'),
    description: t('how.step2.desc')
  }, {
    number: '03',
    title: t('how.step3.title'),
    description: t('how.step3.desc')
  }];
  // Randomized delays and durations — multiple traces start simultaneously from different spots
  const randomParams = useMemo(() => {
    const ringDelays = [0.5, 2.0, 0.8];
    const ringDurations = [8, 10, 7];
    const ringAngles = [45, 220, 310]; // random start points around each circle
    const mobileRingAngles = [120, 300, 30];
    const lineDelays = [0, 1.5];
    const lineDurations = [9, 11];
    return {
      ringDelays,
      ringDurations,
      ringAngles,
      mobileRingAngles,
      lineDelays,
      lineDurations
    };
  }, []);
  return <section className="py-24 relative transition-colors duration-300 overflow-hidden border-t border-stone-400 dark:border-transparent" id="how-it-works" data-id="element-1119" data-mp="3pzd3diuwntfl">
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png)'
    }} data-id="element-1120" data-mp="2tm4j4pbsmh9" />
      <div className="absolute inset-0 bg-black/35" data-id="element-1121" data-mp="crie3s2m79d9" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1122" data-mp="cpxgftl07ab6">
        <div className="text-center mb-24" data-id="element-1123" data-mp="hk1n0bqcxgbq">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight" data-id="element-1124" data-mp="lkc4akjlnt5w">
            {t('how.title.prefix')}{' '}
            <span className="text-[#16B252]" data-id="element-1125" data-mp="15mzo8w69rxua">{t('how.title.highlight')}</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-stone-300 text-lg font-sub max-w-2xl mx-auto" data-id="element-1126" data-mp="10mu21virr3jl">
            {t('how.subtitle')}
          </motion.p>
        </div>

        <div className="relative max-w-5xl mx-auto" ref={sectionRef} data-id="element-1127" data-mp="1oesg2n9lrd0f">
          {/* Desktop Connecting Lines — both start near-simultaneously from different ends */}
          <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-[2px] bg-white/20" data-id="element-1128" data-mp="1eltocwit76cl">
            {/* Line 1: grows left-to-right */}
            <motion.div initial={{
            width: '0%'
          }} animate={isInView ? {
            width: '50%'
          } : {
            width: '0%'
          }} transition={{
            duration: randomParams.lineDurations[0],
            ease: [0.25, 0.1, 0.25, 1],
            delay: randomParams.lineDelays[0]
          }} className="absolute inset-y-0 left-0 rounded-full" style={{
            backgroundColor: 'rgba(46, 138, 68, 0.5)',
            boxShadow: '0 0 4px rgba(46, 138, 68, 0.2)'
          }} data-id="element-1129" data-mp="27376b8c3in06" />
            {/* Line 2: grows left-to-right from midpoint */}
            <motion.div initial={{
            width: '0%'
          }} animate={isInView ? {
            width: '50%'
          } : {
            width: '0%'
          }} transition={{
            duration: randomParams.lineDurations[1],
            ease: [0.25, 0.1, 0.25, 1],
            delay: randomParams.lineDelays[1]
          }} className="absolute inset-y-0 left-[50%] rounded-full" style={{
            backgroundColor: 'rgba(46, 138, 68, 0.5)',
            boxShadow: '0 0 4px rgba(46, 138, 68, 0.2)'
          }} data-id="element-1130" data-mp="1hxfs2cawspt1" />
          </div>

          {/* Mobile Connecting Lines */}
          <div className="md:hidden absolute top-0 bottom-0 left-8 w-[2px] bg-white/20" data-id="element-1131" data-mp="2tcw59knc6zmv">
            <motion.div initial={{
            height: '0%'
          }} animate={isInView ? {
            height: '50%'
          } : {
            height: '0%'
          }} transition={{
            duration: randomParams.lineDurations[0],
            ease: [0.25, 0.1, 0.25, 1],
            delay: randomParams.lineDelays[0]
          }} className="absolute inset-x-0 top-0 rounded-full" style={{
            backgroundColor: 'rgba(46, 138, 68, 0.5)',
            boxShadow: '0 0 4px rgba(46, 138, 68, 0.2)'
          }} data-id="element-1132" data-mp="htzqa2jqabd9" />
            <motion.div initial={{
            height: '0%'
          }} animate={isInView ? {
            height: '50%'
          } : {
            height: '0%'
          }} transition={{
            duration: randomParams.lineDurations[1],
            ease: [0.25, 0.1, 0.25, 1],
            delay: randomParams.lineDelays[1]
          }} className="absolute inset-x-0 top-[50%] rounded-full" style={{
            backgroundColor: 'rgba(46, 138, 68, 0.5)',
            boxShadow: '0 0 4px rgba(46, 138, 68, 0.2)'
          }} data-id="element-1133" data-mp="8v4iwn4y6v8d" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative z-10" data-id="element-1134" data-mp="1rc6fahb9ze6d">
            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            type: 'spring',
            stiffness: 100
          }} className="relative flex flex-col md:items-center pl-20 md:pl-0 h-full" data-id="element-1135" data-mp="1wdc8shcrf5gy" data-mpi={index}>
                {/* Step Number Node */}
                <div className="absolute left-0 md:relative md:left-auto w-16 h-16 md:w-24 md:h-24 bg-black/40 backdrop-blur-sm border-4 border-white/10 rounded-full flex items-center justify-center mb-8 shadow-[0_0_0_2px_rgba(46,138,68,0.2)] z-10 shrink-0" data-id="element-1136" data-mp="1idb5kv90vlyb">
                  <span className="font-heading text-2xl md:text-3xl font-extrabold text-[#FDAC53]" data-id="element-1137" data-mp="pue3ghqyla3o">
                    {step.number}
                  </span>
                  {/* Ring trace - desktop */}
                  <div className="hidden md:block" data-id="element-1138" data-mp="2z3c4k5lsea62">
                    <RingTrace elementSize={96} delay={randomParams.ringDelays[index]} duration={randomParams.ringDurations[index]} isInView={isInView} startAngle={randomParams.ringAngles[index]} data-id="element-1139" data-mp="2u4sfhwbwci9h" />
                  </div>
                  {/* Ring trace - mobile */}
                  <div className="md:hidden" data-id="element-1140" data-mp="2fhfnborl3ea4">
                    <RingTrace elementSize={64} delay={randomParams.ringDelays[index]} duration={randomParams.ringDurations[index]} isInView={isInView} startAngle={randomParams.mobileRingAngles[index]} data-id="element-1141" data-mp="2ffmkylo07g7x" />
                  </div>
                </div>

                {/* Content Card */}
                <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-8 w-full md:text-center border border-white/10 hover:border-[#FDAC53]/30 transition-colors duration-300 relative overflow-hidden group flex-1 flex flex-col shadow-[0_2px_8px_rgba(0,0,0,0.3)]" data-id="element-1142" data-mp="21g3c1qndc0uk">
                  <span className="absolute -bottom-6 -right-4 font-heading text-8xl font-black text-white/5 group-hover:text-[#FDAC53]/10 transition-colors duration-500 pointer-events-none select-none" data-id="element-1143" data-mp="3pem87fzerurv">
                    {step.number}
                  </span>

                  <h3 className="font-heading text-2xl font-bold text-white mb-4 relative z-10" data-id="element-1144" data-mp="3gfime48ux19o">
                    {step.title}
                  </h3>
                  <p className="text-stone-300 leading-relaxed text-[15px] relative z-10" data-id="element-1145" data-mp="36g9y9s0oagv8">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}